import { useState, useCallback } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";

import { usePrevious } from "app/components/hooks/usePrevious";
import { useDidUpdate } from "app/custom-hooks/useDidUpdate";

const useColumnChangeTracker = <RecordType extends object>(
    columns: ColumnsType<RecordType>,
    onColumnChange: VoidFunction,
) => {
    const previousColumns = usePrevious<ColumnsType<RecordType>>(columns);

    useDidUpdate(() => {
        if (!previousColumns) {
            return;
        }

        const currentColumns = columns;

        // Check if columns have actually changed
        if (previousColumns.length !== currentColumns.length) {
            onColumnChange();
        } else {
            const hasChanged = previousColumns.some(
                (prevCol, index) => prevCol.dataIndex !== currentColumns[index].dataIndex,
            );

            if (hasChanged) {
                onColumnChange();
            }
        }
    }, [columns, onColumnChange]);
};

/**
 * This is a **temporary** hook that tracks the column selection and
 * key that changes when columns change, causing the table to unmount/remount,
 * ensuring a complete reset of the table state after column selection changes.
 * !! Should be removed in the scope of **SI-6764**.
 **/
export const useTableUnmountKey = <RecordType extends object>(columns: ColumnsType<RecordType>): number => {
    const [componentKey, setComponentKey] = useState(0);

    const handleColumnChange = useCallback(() => {
        setComponentKey((prev) => prev + 1);
    }, []);

    useColumnChangeTracker(columns, handleColumnChange);

    return componentKey;
};
